import React, { useEffect } from 'react';
import useStore from 'store';
import BaseChart from 'components/BaseChart';
import useSeries from 'pages/IndexBenchmark/hooks/useSeries';
import { useParams } from 'react-router-dom';
import { DEFAULT_METRIC, IndexListItem } from 'store/Index/store';
import { SelectorOption } from 'hooks/chartSelector';
import Box from '@mui/material/Box';
import { useSearchParams } from 'react-router-dom';
import Loading from '@merq/web-ui/dist/components/Loading';
import { PriceHistory } from 'store/PriceHistory/store';
import dayjs from 'dayjs';

const IndexLevelsIframe = () => {
  const params = useParams<{ indexName: string; language: string }>();
  const [searchParams] = useSearchParams();

  const height = searchParams.get('height') ?? '600px';

  const series = useSeries();
  const [
    getIndexByName,
    indexState,
    initPriceHistory,
    indexListState,
    getIndices,
    chartState,
    priceHistoryState,
  ] = useStore(state => [
    state.getIndexByName,
    state.index,
    state.initPriceHistory,
    state.indices,
    state.getIndices,
    state.chartState,
    state.priceHistory,
  ]);

  const selectedIndex = indexState.selected;
  const indices = indexListState.items;

  const colors = (searchParams.get('colors') ?? '').split(',');
  const legend = (searchParams.get('legend') ?? 'true') === 'true';

  const indexName = parseIndexName(params.indexName);
  const extras: string[] = [];

  useEffect(() => {
    getIndices();
  }, []);

  useEffect(() => {
    if (indices.length && !chartState.options.length) {
      chartState.initSeries(indices, extras);
    }
  }, [indices.length]);

  useEffect(() => {
    if (indexName) {
      getIndexByName(indexName);
    }
  }, [indexName]);

  useEffect(() => {
    if (selectedIndex && selectedIndex.id) {
      chartState.initFromIndex(selectedIndex, extras);
      // TODO: include  query string benchmarks in the initial call and remove the useEffect from StateBar

      if (extras.length && indices.length) {
        const extraOptions =
          getExtraOptions(indices, extras, selectedIndex.plotMetric || DEFAULT_METRIC) || [];
        initPriceHistory(selectedIndex, extraOptions);
      } else if (!extras.length) {
        initPriceHistory(selectedIndex);
      }
    }
  }, [(selectedIndex || {}).id, indices.length]);

  const pointFormatter = (
    point: any,
    _disableMetrics: boolean,
    _defaultMetric: string,
    roundingDecimals = 2
  ): string => {
    const value = Number(point.y).toFixed(roundingDecimals);

    return `<b>${point.series.name}: ${value}</b>`;
  };

  const lastPoint = getLastPoint(selectedIndex?.id, priceHistoryState);
  const value = lastPoint ? String(lastPoint?.[1].toFixed(2)) : undefined;
  const bigFormat = 'ddd, MMM DD, YYYY';
  const smallFormat = 'MMM DD, YYYY';
  const format = window.innerWidth < 400 ? smallFormat : bigFormat;
  const date = lastPoint ? dayjs.utc(lastPoint[0]).format(format) : undefined;

  return (
    <Loading
      active={indexListState.status === 'loading' || priceHistoryState.status === 'loading'}
      paper
      height={height}
    >
      {!!series.length && selectedIndex && (
        <Box sx={{ backgroundColor: 'background.paper', p: 2, pb: 1 }}>
          <BaseChart
            displayLogo
            height={height}
            series={series as any}
            onLoad={() => {}}
            defaultIndex={selectedIndex}
            title={selectedIndex?.title ?? ''}
            value={`INDEX LEVEL: ${value}`}
            date={date}
            customOptions={{
              exporting: {
                enabled: false,
              },
              legend: {
                enabled: legend && series.length > 1,
              },
            }}
            palette={colors}
            isLoggedIn={true}
            compareMode="percent"
            pointFormatter={pointFormatter}
          />
        </Box>
      )}
    </Loading>
  );
};

export default IndexLevelsIframe;

const parseIndexName = (indexName = '') =>
  indexName.includes('=') ? indexName.split('=')[1] : indexName;

const getExtraOptions = (
  indices: IndexListItem[],
  extras: string[],
  metric: string
): SelectorOption[] | null => {
  if (!indices.length) return null;
  const options = extras
    .map(indexName => {
      const index = indices.find(i => i.name === indexName);
      if (!index) return null;
      return {
        label: index.name,
        value: index.id,
        type: 'index',
        isRealTime: false,
        metric,
      };
    })
    .filter(Boolean) as SelectorOption[];

  return options;
};

const getLastPoint = (indexId: string | undefined, history: PriceHistory) => {
  if (!indexId) return null;
  const selected = history.results[indexId]?.price_return ?? [];
  return selected && selected.length ? selected[selected.length - 1] : null;
};
